import React from 'react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

const joinClassNames = (...values: Array<string | undefined>) => values.filter(Boolean).join(' ');

const isStrongOnlyParagraph = (children: React.ReactNode): boolean => {
    const nodes = React.Children.toArray(children).filter((node) => {
        if (typeof node !== 'string') return true;
        return node.trim().length > 0;
    });

    if (nodes.length !== 1) return false;
    const only = nodes[0];
    return React.isValidElement(only) && only.type === 'strong';
};

const OrderedList: React.FC<React.OlHTMLAttributes<HTMLOListElement>> = ({ className, children, ...rest }) => (
    <ol className={joinClassNames('agent__mdList agent__mdList--ordered', className)} {...rest}>
        {children}
    </ol>
);

const UnorderedList: React.FC<React.HTMLAttributes<HTMLUListElement>> = ({ className, children, ...rest }) => (
    <ul className={joinClassNames('agent__mdList agent__mdList--unordered', className)} {...rest}>
        {children}
    </ul>
);

const ListItem: Components['li'] = ({ className, children }) => (
    <li className={joinClassNames('agent__mdListItem', className)}>{children}</li>
);

const Heading: React.FC<React.HTMLAttributes<HTMLDivElement> & { level: number }> = ({
    className,
    level,
    children,
    ...rest
}) => {
    const headingClassName = joinClassNames('agent__mdHeading', `agent__mdHeading--h${level}`, className);
    return (
        <div className={headingClassName} {...rest}>
            {children}
        </div>
    );
};

interface ChatMarkdownProps {
    className?: string;
    children: string;
    components?: Components;
}

interface MarkdownHeadingProps extends React.HTMLAttributes<HTMLHeadingElement> {
    node?: unknown;
}

const createHeadingComponent =
    (level: number) =>
    ({ className, children, ...rest }: MarkdownHeadingProps) => (
        <Heading level={level} className={className} {...rest}>
            {children}
        </Heading>
    );

// tslint:disable:no-magic-numbers
const headingComponents: Components = {
    h1: createHeadingComponent(1),
    h2: createHeadingComponent(2),
    h3: createHeadingComponent(3),
    h4: createHeadingComponent(4),
    h5: createHeadingComponent(5),
    h6: createHeadingComponent(6)
};
// tslint:enable:no-magic-numbers

const defaultComponents: Components = {
    a: ({ node, ...props }) => {
        String(node);
        return <a {...props} rel="noopener noreferrer" target="_blank" />;
    },
    code: ({ children, className }) => <code className={className}>{children}</code>,
    li: ListItem,
    ol: ({ className, children, start }) => (
        <OrderedList className={className} start={start}>
            {children}
        </OrderedList>
    ),
    p: ({ className, children }) => {
        if (isStrongOnlyParagraph(children)) {
            const sectionTitleClassName = joinClassNames('agent__mdSectionTitle', className);
            return <div className={sectionTitleClassName}>{children}</div>;
        }

        const paragraphClassName = joinClassNames('agent__mdParagraph', className);
        return <div className={paragraphClassName}>{children}</div>;
    },
    table: ({ node, className, children, ...rest }) => {
        String(node);
        return (
            <div className="agent__mdTableWrap">
                <div className="agent__mdTableScroll">
                    <table className={joinClassNames('agent__mdTable', className)} {...rest}>
                        {children}
                    </table>
                </div>
            </div>
        );
    },
    ul: ({ className, children }) => <UnorderedList className={className}>{children}</UnorderedList>,
    ...headingComponents
};

export const ChatMarkdown: React.FC<ChatMarkdownProps> = React.memo(({ className, children, components }) => {
    const mergedComponents = React.useMemo<Components>(
        () => ({
            ...defaultComponents,
            ...components
        }),
        [components]
    );

    const markdownNode = (
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={mergedComponents}>
            {children}
        </ReactMarkdown>
    );

    if (!className) return markdownNode;

    return <div className={className}>{markdownNode}</div>;
});

ChatMarkdown.displayName = 'ChatMarkdown';
